import System from '../System/System';
import { Random } from '../Utility/Random';
import { TickerEvent } from '../Utility/Ticker';
import Game from '../Game';
import GameObject, { GameObjectEvent } from '../GameObject/GameObject';
import EventEmitter from 'eventemitter3';
import { Constructor } from '../Utility/Types';
import { IGameState } from '../State/Game/IGameState';
import { report } from '../report';
import { promisifyEvent, sleep } from '../Utility/async';
import { FramesHash } from '../Utility/Resource/IResourceDriver';
import { handleTrackTimeToFirstInteractionPossible } from '../Utility/Tracking/Track';
import { preloadSceneId, transitionSceneId } from '../State/Game/GameScene';
import { SceneTransition } from './SceneManager';
import { PlayerActions } from '../State/Player/PlayerActions';

export interface ISceneParams {
    [key: string]: any;
}

export enum SceneEvent {
    GameObjectAdded = 'gameObjectAdded',
    GameObjectRemoved = 'gameObjectRemoved',
}

export default class Scene extends EventEmitter {
    public readonly id: string;
    public index: number;
    private readonly gameObjects: GameObject[];
    public systems: System[];
    public params: ISceneParams;
    public loaded: boolean;
    public active: boolean;
    public stopping: boolean;

    constructor(index: number = 0) {
        super();
        this.id = `${this.constructor.name}_${Random.ID()}`;
        this.gameObjects = [];
        this.systems = [];
        this.loaded = false;
        this.active = false;
        this.index = index;
    }

    /**
     * Called every time the scene is started before preload (if not already loaded).
     */
    public init(): void {}

    /**
     * Called when scene is started. Load any required assets for the scene.
     */
    public async preload() {
        // Preload systems in order they are added.
        for (let system of this.systems) {
            await system.preload();
        }
    }

    /**
     * Remove all gameobjects and then remove all systems. Systems removed last so gameobjects know which systems
     * to be removed from as well.
     */
    public unload() {
        console.info(`[Scene] Unloading '${this.id}'`);
        this.stop();

        // Filter and destroy root game object.
        // They'll destroy their children and so on.
        const rootGameObjects = this.gameObjects.filter(go => !go.parent);
        for (let i = rootGameObjects.length - 1; i >= 0; i--) {
            rootGameObjects[i].destroy();
        }

        this.systems.splice(0).forEach(system => {
            system.destroy();
            this.removeSystem(system);
        });

        this.removeAllListeners();
    }

    public start() {
        this.active = true;

        // should be before system start
        this.trackTimeUntilFirstUserInteractionPossible();

        // Start all systems.
        this.systems.forEach(system => system.start());

        // After all systems have been started prepare the current scene.
        this.systems.forEach(system => {
            system.handleStateChanged(system.store.getState());
        });

        // Start update loop.
        Game.ticker.on(TickerEvent.TICK, this.update);

        Game.resource.loadFullQualityAtlases().then(this.onFullQualityAssetsLoaded);
        Game.resource.startOtherDriversLoad();
    }

    private async trackTimeUntilFirstUserInteractionPossible(): Promise<void> {
        const scenesToSkip = [transitionSceneId, preloadSceneId].map(id => Game.scene.getScene(id));

        if (scenesToSkip.includes(this)) return;

        const transitionScene = Game.scene.getScene(transitionSceneId);

        const action = await Promise.race([
            Game.store.waitFor(PlayerActions.showLevelAutoFlowPopup),
            promisifyEvent(transitionScene, SceneTransition.ENDED),
        ]);

        if (action) {
            while (!Game.store.getState().UIState.popupID) {
                await sleep(1);
            }

            await sleep(500); // Menu appearing time
        }

        handleTrackTimeToFirstInteractionPossible();
    }

    private readonly onFullQualityAssetsLoaded = async (framesHash: FramesHash): Promise<void> => {
        for (const gameObject of this.gameObjects) {
            if (gameObject.destroyed || !gameObject.container || !(gameObject.container instanceof PIXI.Sprite))
                continue;

            const frameName = gameObject.container.texture.textureCacheIds[0];

            if (framesHash[frameName]) {
                gameObject.container.texture = PIXI.Texture.from(frameName);
                await sleep(1);
            }
        }
    };

    public stop() {
        if (this.stopping) {
            report('Can not stop scene as it is not currently active or in the process of stopping', {
                once: true,
                tags: { sceneId: this.id },
            });
            return;
        }

        console.info(`[Scene] Stopping '${this.id}'`);
        this.stopping = true;
        this.active = false;

        try {
            for (let i = 0, n = this.systems.length; i < n; i++) {
                this.systems[i].stop();
            }
        } catch (error) {
            report('Error when stopping systems', {
                error,
                once: true,
                tags: { sceneId: this.id },
            });
        }

        // Start update loop.
        Game.ticker.off(TickerEvent.TICK, this.update);

        this.stopping = false;
    }

    public readonly update = () => {
        for (let i = 0, n = this.systems.length; i < n; i++) {
            if (this.systems[i]?.isActive()) {
                this.systems[i].update();
            }
        }

        for (let i = 0, n = this.systems.length; i < n; i++) {
            if (this.systems[i]?.isActive()) {
                this.systems[i].lateUpdate();
            }
        }
    };

    public readonly addGameObject = (gameObject: GameObject): void => {
        if (this.hasGameObject(gameObject)) return;

        gameObject.scene = this;
        this.gameObjects.push(gameObject);

        gameObject.on(GameObjectEvent.CHILD_ADDED, this.addGameObject);
        gameObject.on(GameObjectEvent.CHILD_REMOVED, this.removeGameObject);
        gameObject.on(GameObjectEvent.DESTROYED, this.removeGameObject);

        gameObject.children.forEach(this.addGameObject);

        this.emit(SceneEvent.GameObjectAdded, gameObject);
    };

    public hasGameObject(gameObject: GameObject): boolean {
        return this.gameObjects.includes(gameObject);
    }

    public readonly removeGameObject = (gameObject: GameObject): void => {
        if (!this.hasGameObject(gameObject)) return;

        const index = this.gameObjects.indexOf(gameObject);
        if (index >= 0) this.gameObjects.splice(index, 1);

        gameObject.off(GameObjectEvent.CHILD_ADDED, this.addGameObject);
        gameObject.off(GameObjectEvent.CHILD_REMOVED, this.removeGameObject);
        gameObject.off(GameObjectEvent.DESTROYED, this.removeGameObject);

        gameObject.children.forEach(this.removeGameObject);

        gameObject.scene = null;
        this.emit(SceneEvent.GameObjectRemoved, gameObject);
    };

    public addSystem(system: System): void {
        if (this.hasSystem(system)) return;

        this.systems.push(system);
        system.scene = this;
    }

    public hasSystem(system: System): boolean {
        return this.systems.indexOf(system) !== -1;
    }

    public removeSystem(system: System): void {
        if (!this.hasSystem(system)) return;

        const index = this.systems.indexOf(system);
        if (index >= 0) this.systems.splice(index, 1);

        system.scene = null;
    }

    public getSystem<T extends System>(systemType: Constructor<T>): T {
        return this.systems.find((system: System) => system instanceof systemType) as T;
    }

    public toString(): string {
        return this.id;
    }

    public handleStateChange(state: IGameState) {
        for (let i = 0, n = this.systems.length; i < n; i++) {
            this.systems[i].handleStateChanged(state);
        }
    }
}
